import {
  IonIcon,
  IonRouterOutlet,
  IonSpinner,
  IonSplitPane,
  setupIonicReact,
} from '@ionic/react';
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css'; /* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import { PostHogProvider } from '@posthog/react';
import React, {
  Profiler,
  startTransition,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactGA from 'react-ga4';
import { useSelector } from 'react-redux';
import { Switch } from 'react-router';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import Footer from './components/footer/Footer';
import ImportProgressConnector from './components/globalProgressBar/ImportProgressConnector';
import Header from './components/header/Header';
import Menu from './components/menu/Menu';
import LoginOverlay from './pages/auth/login/login_overlay';
import CreateCrate from './pages/create-crate/create-crate';
import Index from './pages/Index';
import GenerateCrate from './pages/myCrates/generate-crate/generate-crate';
import Page from './pages/PageTemplate';

import { useDispatch } from './store/hooks'; // Typed dispatch
import {
  AppIdentifiers,
  getToken,
  setToken,
  unsetToken,
} from './store/slices/token-state.slice';

import {
  CrateControllerSearchRequest,
  GetCratesDto,
  SearchControllerSearchRequest,
} from '@cratehackers/api-client';
import { FolderManager } from './components/common/FolderManager';
import { MusicSourcesManager } from './components/common/MusicSourcesManager';
import FeatureInProgress from './components/featureInProgress/FeatureInProgress';
import {
  GlobalAlertProvider,
  useGlobalAlert,
} from './components/GlobalAlert/GlobalAlertContext';
import KartraUpgrade from './components/kartra/KartraUpgrade';
import { RootLoader } from './components/RootLoader/RootLoader';
import ScanningIndicator from './components/scanning/ScanningIndicator';
import ClientSupportBanner from './components/clientSupport/ClientSupportBanner';
import MembershipClaimsBootstrap from './components/clientSupport/MembershipClaimsBootstrap';
import VdjCacheIndexBootstrap from './components/tracks/VdjCacheIndexBootstrap';
import SearchResults from './components/searchResults/SearchResults';
import { default as SpotifyCallback } from './components/spotify/callback';
import { default as TidalCallback } from './components/tidal/callback';

import AppReleaseModals from './components/appRelease/AppReleaseModals';
import { ToastManager } from './components/ToastManager/ToastManager';
import StickyPlayer from './components/waveform/StickyPlayer';
import SetupWizard from './components/wizard/SetupWizard';
import { config } from './config';
import { BreakpointContextProvider } from './context/breakpoint/BreakpointContext';
import { ImportProgressProvider } from './context/ImportProgressContext';
import { LibraryStatsProvider } from './context/LibraryStatsContext';
import { on, removeListener, run } from './helper/electron';
import { useMemoryManager } from './hooks/useMemoryManager';
import { useStartupFlags } from './hooks/useStartupFlags';
import DuplicateResolverPage from './pages/duplicates/DuplicateResolverPage';
import BugReportPage from './pages/feedback/bug-report';
import FeatureRequestPage from './pages/feedback/feature-request';
import FolderFlattener from './pages/folder-flattener/folder-flattener';
import HealthReportPage from './pages/health-report/HealthReportPage';
import MyLibrary from './pages/my-library/MyLibrary';
import Preferences from './pages/preferences/Preferences';
import RecycleBinPage from './pages/recycle-bin/RecycleBinPage';
import AutomationsPage from './pages/automations/AutomationsPage';
import AutomationEditorPage from './pages/automations/AutomationEditorPage';
import { checkLnrpStatus } from './store/slices/lnrp.slice';
import { checkDmsStatus } from './store/slices/dms.slice';
import {
  selectShowPreferences,
  setShowPreferences,
} from './store/slices/preferences.slice';

import { linkOutline } from 'ionicons/icons';
import { AgentChatSessionProvider } from './components/agentChat/AgentChatSession';
import { ScrollProvider } from './components/scrollContext/ScrollContext';
import ApiService from './services/Api.service';
import MembershipGuardService from './services/MembershipGuard.service';
import PostHogService from './services/PostHog.service';
import { clearCrate } from './store/slices/crate.slice';
import { clearTrackMatches } from './store/slices/track-matcher.slice';
import { resetTrackMatcherBoundCrateId } from './utils/shouldClearTrackMatchesForCrateId';
import {
  cancelWizardDismissed,
  selectWizardDismissed,
  setWizardDismissed,
} from './store/slices/ui.slice';
import { RootState } from './store/store';
import { DriveStateSubscriber } from './store/subscribers/drive-state.subscriber';
import './theme/variables.css';
import { iCategory } from './types/ICategory';
import { iCrate } from './types/ICrate';
import { iHacker } from './types/IHackers';
import { iTrack } from './types/ITrack';
import { isTokenValid } from './utils/auth';
import isElectron from './utils/isElectron';
import { logout } from './utils/logout';

const Register = React.lazy(() => import('./pages/auth/register/register'));
const LogOut = React.lazy(() => import('./pages/auth/logout/logout'));
const Account = React.lazy(() => import('./pages/account/Account'));
const Login = React.lazy(() => import('./pages/auth/login/login'));
const Crates = React.lazy(() => import('./pages/crates/Crates'));
const Crate = React.lazy(() => import('./pages/crate/Crate'));
const Track = React.lazy(() => import('./pages/track/SingleTrack'));
const MyCratesPage = React.lazy(() => import('./pages/myCrates/myCrates'));
const Charts = React.lazy(() => import('./pages/charts/Charts'));
const Chart = React.lazy(() => import('./pages/charts/Chart'));
const Category = React.lazy(() => import('./pages/category/Category'));
const Marketplace = React.lazy(() => import('./pages/marketplace/Marketplace'));

const Announcement = React.lazy(
  () => import('./pages/announcement/Announcement'),
);
const ForgotPassword = React.lazy(
  () => import('./pages/auth/forgotPassword/forgotPassword'),
);

const ResetPassword = React.lazy(
  () => import('./pages/auth/resetPassword/resetPassword'),
);
const VerifyAccount = React.lazy(
  () => import('./pages/auth/verifyAccount/verifyAccount'),
);
const BusinessPlanDashboard = React.lazy(
  () => import('./pages/business-plan/BusinessPlanDashboard'),
);
const Team = React.lazy(() => import('./pages/business-plan/Team'));
const TeamCrates = React.lazy(() => import('./pages/team/TeamCrates'));
const UserDevices = React.lazy(
  () => import('./pages/business-plan/UserDevices'),
);
const BangerBox = React.lazy(() => import('./pages/business-plan/BangerBox'));
const BangerBoxFullscreen = React.lazy(
  () => import('./pages/business-plan/BangerBoxFullscreen'),
);
const BangerVisionSessions = React.lazy(
  () => import('./pages/banger-vision/BangerVisionSessions'),
);
const BangerVisionAnalytics = React.lazy(
  () => import('./pages/banger-vision/BangerVisionAnalytics'),
);
const CrateHackersLabs = React.lazy(
  () => import('./pages/business-plan/CrateHackersLabs'),
);
const HackersLabs = React.lazy(
  () => import('./pages/business-plan/HackersLabs'),
);
const HackersLabsAdmin = React.lazy(
  () => import('./pages/business-plan/HackersLabsAdmin'),
);
const GroupManagement = React.lazy(
  () => import('./pages/admin/GroupManagement'),
);
const AnnouncementsAdmin = React.lazy(
  () => import('./pages/admin/AnnouncementsAdmin'),
);
const TutorialVideosAdmin = React.lazy(
  () => import('./pages/admin/TutorialVideosAdmin'),
);
const KnownIssuesAdmin = React.lazy(
  () => import('./pages/admin/KnownIssuesAdmin'),
);
const AppManagement = React.lazy(() => import('./pages/admin/AppManagement'));
const CreateGroup = React.lazy(() => import('./pages/admin/CreateGroup'));
const EditGroup = React.lazy(() => import('./pages/admin/EditGroup'));
const AddMember = React.lazy(() => import('./pages/admin/AddMember'));

// Serato components
const SeratoCratesList = React.lazy(
  () => import('./pages/serato-crates/SeratoCratesList'),
);
const SeratoCrateTracks = React.lazy(
  () => import('./pages/serato-crates/SeratoCrateTracks'),
);
const SeratoCrateTracksView = React.lazy(
  () => import('./pages/serato-crates/SeratoCrateTracksView'),
);

// Pipelines component
const Pipelines = React.lazy(() => import('./pages/pipelines/Pipelines'));

setupIonicReact();

const publicRoutes = () => (
  <Suspense fallback={<IonSpinner name="dots" />}>
    <IonRouterOutlet id="main">
      <Switch>
        <Route path="/register" exact>
          <Register />
        </Route>
        <Route path="/verify/:token" exact>
          <VerifyAccount />
        </Route>
        <Route path="/forgot-password" exact>
          <ForgotPassword />
        </Route>
        <Route path="/reset-password/:token" exact>
          <ResetPassword />
        </Route>
        <Route path="/login" exact>
          <Login />
        </Route>
        <Route path="*">
          <Login />
        </Route>
      </Switch>
    </IonRouterOutlet>
  </Suspense>
);

const privateRoutes = (
  token: any,
  history: ReturnType<typeof useHistory>,
  dispatch: ReturnType<typeof useDispatch>,
) => (
  <Suspense fallback={<IonSpinner name="dots" />}>
    <IonRouterOutlet id="main">
      <ScanningIndicator />
      <Switch>
        <Route exact path="/" component={Index} />
        <Route exact path="/account" component={Account} />
        <Route exact path="/announcement/:url" component={Announcement} />
        <Route exact path="/charts/favorites" component={Charts} />
        <Route exact path="/charts" component={Charts} />
        <Route exact path="/chart/:id" component={Chart} />
        <Route exact path="/crates" component={Crates} />
        <Route exact path="/crates/team/:groupId?" component={TeamCrates} />
        <Route exact path="/crate/:id" component={Crate} />
        <Route exact path="/crate/:id/export" component={Crate} />
        <Route exact path="/category/:id" component={Category} />

        {/* Serato Crates Routes */}
        <Route exact path="/serato-crates" component={SeratoCratesList} />
        <Route
          exact
          path="/serato-crates/:crateFile"
          component={SeratoCrateTracks}
        />
        <Route
          exact
          path="/serato-crate-tracks/:crateFile"
          component={SeratoCrateTracksView}
        />
        <Route exact path="/track" component={Track} />
        <Route exact path="/genres" component={FeatureInProgress} />
        <Route exact path="/marketplace" component={Marketplace} />
        <Route exact path="/pipelines" component={Pipelines} />
        <Route exact path="/logout" component={LogOut} />

        {/* Setup wizard (only if logged in) */}
        <Route
          exact
          path="/setup"
          render={() =>
            token?.accessToken ? (
              <SetupWizard
                onComplete={(route: string) => {
                  // Mark that setup has been completed at least once
                  localStorage.setItem('hasCompletedSetup', 'true');
                  dispatch(setWizardDismissed());
                  history.push(route ? route : '/');
                }}
              />
            ) : (
              <Redirect to="/login" />
            )
          }
        />

        <Route exact path="/my-crates" component={MyCratesPage} />
        <Route
          exact
          path="/my-crates/:buildType/:crateId"
          component={GenerateCrate}
        />
        <Route
          exact
          path="/my-crates/crate/:crateId/:buildType"
          component={GenerateCrate}
        />
        <Route
          exact
          path="/my-crates/crate/:crateId"
          component={GenerateCrate}
        />
        <Route
          path="/my-crates/:sub(genre|home|serato|smpl|djep|spotify|text|file|track)"
          component={CreateCrate}
        />

        <Route exact path="/my-library" component={MyLibrary} />
        <Route
          exact
          path="/my-library/health-report"
          component={HealthReportPage}
        />
        <Route exact path="/my-library/sources">
          <div className="page-container">
            <div className="page-header">
              <h1>
                <IonIcon icon={linkOutline} style={{ marginRight: '10px' }} />
                Music Sources
              </h1>
              <p className="page-description">Manage your music folders</p>
            </div>
            <MusicSourcesManager />
          </div>
        </Route>
        <Route
          path="/resolve-duplicates"
          component={DuplicateResolverPage}
          exact={true}
        />
        <Route path="/recycle-bin" component={RecycleBinPage} exact={true} />
        <Route exact path="/automations" component={AutomationsPage} />
        <Route exact path="/automations/new" component={AutomationEditorPage} />
        <Route exact path="/automations/:id" component={AutomationEditorPage} />
        <Route exact path="/page/:name" component={Page} />
        <Route path="/spotify/callback" component={SpotifyCallback} />
        <Route path="/tidal/callback" component={TidalCallback} />

        <Route
          exact
          path="/tools/folder-flattener"
          component={FolderFlattener}
        />
        <Route
          exact
          path="/tools/shazam-to-crate"
          component={FeatureInProgress}
        />
        <Route
          exact
          path="/tools/spotify-import"
          component={FeatureInProgress}
        />
        <Route exact path="/tools/text-import" component={FeatureInProgress} />
        <Route exact path="/vip-crates" component={FeatureInProgress} />
        <Route exact path="/my-library/folders">
          <div className="page-container">
            <FolderManager
              mode="management"
              title="Manage Music Folders"
              description="Add, remove, and manage your music source folders"
              showAdvancedOptions={true}
              allowBulkOperations={true}
              autoRefreshLibrary={true}
            />
          </div>
        </Route>

        <Route exact path="/feedback/bug" component={BugReportPage} />
        <Route
          exact
          path="/feedback/feature-request"
          component={FeatureRequestPage}
        />

        {/* Admin Routes */}
        <Route
          exact
          path="/admin/group-management"
          component={GroupManagement}
        />
        <Route exact path="/admin/create-group" component={CreateGroup} />
        <Route exact path="/admin/edit-group/:groupId" component={EditGroup} />
        <Route exact path="/admin/add-member/:groupId" component={AddMember} />
        <Route
          exact
          path="/admin/announcements"
          component={AnnouncementsAdmin}
        />
        <Route
          exact
          path="/admin/tutorial-videos"
          component={TutorialVideosAdmin}
        />
        <Route exact path="/admin/known-issues" component={KnownIssuesAdmin} />
        <Route exact path="/admin/app-management" component={AppManagement} />

        {/* Business Plan Routes */}
        <Route exact path="/labs" component={HackersLabs} />
        <Route exact path="/labs/admin" component={HackersLabsAdmin} />
        <Route exact path="/business-plan" component={BusinessPlanDashboard} />
        <Route exact path="/business-plan/team" component={Team} />
        <Route
          exact
          path="/business-plan/team/:userId/devices"
          component={UserDevices}
        />

        <Route exact path="/business-plan/bangerbox" component={BangerBox} />
        <Route
          exact
          path="/business-plan/bangerbox-fullscreen"
          component={BangerBoxFullscreen}
        />
        <Route exact path="/crate-hackers-labs" component={CrateHackersLabs} />
        <Route exact path="/banger-vision" component={BangerVisionSessions} />
        <Route
          exact
          path="/banger-vision/analytics/:sessionId"
          component={BangerVisionAnalytics}
        />

        {/* V11.0.0 Routes */}
        <Redirect to="/" />
      </Switch>
    </IonRouterOutlet>
  </Suspense>
);

// Override window.alert to use the custom global alert
function AlertOverride() {
  const { showAlert } = useGlobalAlert();
  React.useEffect(() => {
    window.alert = (msg) => {
      showAlert(msg);
    };
  }, [showAlert]);
  return null;
}

// React Profiler callback for performance monitoring
const onRenderCallback = (
  id: string,
  phase: string,
  actualDuration: number,
  baseDuration: number,
  startTime: number,
  commitTime: number,
) => {
  if (actualDuration > 16) {
    // Log renders taking longer than 16ms (60fps threshold)
    console.log(`🐌 Slow render - ${id}:`, {
      phase,
      actualDuration: `${actualDuration.toFixed(2)}ms`,
      baseDuration: `${baseDuration.toFixed(2)}ms`,
      startTime,
      commitTime,
    });
  }
};

const App: React.FC = () => {
  const history = useHistory();

  useEffect(() => {
    if (process.env.REACT_APP_GA_ID) {
      ReactGA.initialize(process.env.REACT_APP_GA_ID, {
        // testMode: true, // TODO: For development mode only
      });
    }

    // PostHog is initialized via PostHogProvider - no manual init needed
  }, []);

  useEffect(() => {
    const capturePageview = () => {
      const { pathname, search, hash } = history.location;
      const hashSegment =
        typeof window !== 'undefined' && window.location.hash
          ? window.location.hash
          : hash;
      const basePath = `${pathname}${search}${hash ? hash : ''}`;

      if (isElectron()) {
        const canonicalHash =
          hashSegment && hashSegment.startsWith('#')
            ? hashSegment
            : `#${pathname.replace(/^\//, '') || ''}`;
        PostHogService.capturePageview(
          `cratehackers://${canonicalHash}${search}`,
        );
        return;
      }

      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : '';
      PostHogService.capturePageview(`${origin}${basePath}`);
    };

    capturePageview();
    const unlisten = history.listen(() => {
      capturePageview();
    });
    return () => {
      unlisten();
    };
  }, [history]);

  const token = useSelector(
    (state: RootState) => state.tokenState[AppIdentifiers.App],
  );
  const wizardDismissed = useSelector(selectWizardDismissed);
  const { softwareSelected, hasScannedTracks, isLoading } = useStartupFlags();

  // useUserflow();

  // Enable global memory management
  useMemoryManager();

  const dispatch = useDispatch();
  useEffect(() => {
    const appToken = getToken('app');
    if (appToken && isTokenValid(appToken)) {
      startTransition(() => {
        dispatch(
          setToken(
            AppIdentifiers.App,
            appToken.accessToken,
            appToken.refreshToken,
            appToken.expiration,
          ),
        );

        // TODO: Get user info from API when a token exists.
        const email = localStorage.getItem('email');
        if (!email) {
          logout();
        }

        if (ReactGA.isInitialized) {
          const userProperties = {
            app_version: config.VERSION,
            email: '',
          };

          if (email) {
            userProperties.email = email;
          }

          ReactGA.gtag('set', 'user_properties', userProperties);
        }

        // Identify user in PostHog
        if (PostHogService.isInitialized() && email) {
          PostHogService.identify(email, {
            email: email,
            app_version: config.VERSION,
          });
          PostHogService.setPersonProperties({
            email: email,
            app_version: config.VERSION,
          });
        }

        dispatch(checkLnrpStatus());
        dispatch(checkDmsStatus());
      });
    } else {
      dispatch(unsetToken(AppIdentifiers.App));
      if (isElectron()) {
        setShowLogin(true);
      } else {
        logout();
      }
    }
  }, [dispatch]);

  const apiClient = ApiService.getApiClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [crateSearchResults, setCrateSearchResults] = useState<iCrate[]>([]);
  const [songSearchResults, setSongSearchResults] = useState<any[]>([]); // TODO: Get SimpleTrackDto
  const [searchLoading, setSearchLoading] = useState(false);
  const location = useLocation();

  const showSettings = useSelector(selectShowPreferences);

  useEffect(() => {
    setCrateSearchResults([]);
    setSongSearchResults([]);
    setSearchQuery('');
    dispatch(setShowPreferences(false));
  }, [location]);

  useMemo(() => {
    const appToken = getToken('app');

    if (!appToken || !isTokenValid(appToken)) {
      if (isElectron()) {
        setShowLogin(true);
      } else {
        logout();
      }
    } else setShowLogin(false);
  }, [location]);

  useEffect(() => {
    if (isElectron()) {
      on('app/before-quit', () => {
        try {
          const appToken = getToken('app');
          if (!appToken || !isTokenValid(appToken)) {
            logout();
          }
        } finally {
          run('app/quit-confirmed');
        }
      });

      return () => {
        removeListener('app/before-quit');
      };
    }
  }, []);

  // Ensure the login overlay is cleared once a valid token exists
  useEffect(() => {
    if (token?.accessToken) {
      setShowLogin(false);
    }
  }, [token?.accessToken]);

  useEffect(() => {
    searchQuery.trim().length > 0 && dispatch(setShowPreferences(false));
  }, [searchQuery]);

  // Initialize membership guard for client-side subscription checking
  useEffect(() => {
    if (token?.accessToken) {
      console.log('[App] Initializing membership guard for logged-in user');
      const navigationListener =
        MembershipGuardService.initializeWithNavigation();

      // Listen for navigation changes to trigger membership checks
      const unsubscribe = history.listen(() => {
        navigationListener();
      });

      return () => {
        unsubscribe();
      };
    }
  }, [token?.accessToken, history]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim().length < 3) {
        setCrateSearchResults([]);
        setSongSearchResults([]);
        return;
      }

      const requestCrates: CrateControllerSearchRequest = {
        crateSearchDto: {
          term: searchQuery,
          offset: 0,
        },
      };
      const requestSongs: SearchControllerSearchRequest = {
        searchDto: {
          term: searchQuery,
          objectType: 'track',
          offset: 0,
          additionalParams: {
            markets: [],
          },
          raw: false,
          limit: 8,
        },
      };

      setSearchLoading(true);

      apiClient
        .searchControllerSearch(requestSongs)
        .then((results: any) => {
          if (results.items.length > 0) {
            const tracks = results.items.map(
              (track: any): iTrack => ({
                Artist: track?.artist?.name,
                Title: track?.title,
                AlbumCover: track?.album?.picture,
                Key_Camelot: track?.key,
                Key: track?.key,
                PreviewURL: track?.previewUrl,
                BPM: track?.bpm,
                ID: track?.uuid,
                keyMatches: {
                  energyBoost: track?.keys?.EnergyBoost,
                  energyDrop: track?.keys?.EnergyDrop,
                  moodChange: track?.keys?.MoodChange,
                  perfectMatch: track?.keys?.PerfectMatch,
                },
              }),
            );
            setSongSearchResults(tracks);
          } else {
            setSongSearchResults([]);
          }
          setSearchLoading(false);
        })
        .catch(() => {
          setSongSearchResults([]);
        });

      apiClient
        .crateControllerSearch(requestCrates)
        .then((data: GetCratesDto) => {
          const crateResults: iCrate[] = data.results.map((crate) => {
            return {
              _id: crate.id,
              Title: crate.title,
              Image: crate.image,
              Hacker: crate.hacker as iHacker[],
              Category: crate.category as iCategory[],
            };
          });

          setCrateSearchResults(crateResults);
          setSearchLoading(false);
        })
        .catch((error: any) => {
          console.error(error);
          setCrateSearchResults([]);
          setSearchLoading(false);
        });
    }, 750); // Delay in milliseconds

    return () => clearTimeout(delayDebounceFn); // Cleanup the timeout on unmount or when searchQuery changes
  }, [searchQuery]);

  const handleItemClick = () => {
    dispatch(clearCrate());
    resetTrackMatcherBoundCrateId();
    dispatch(clearTrackMatches());
    setCrateSearchResults([]);
    setSongSearchResults([]);
    setSearchQuery('');
  };

  const handleSearchClose = () => {
    setCrateSearchResults([]);
    setSongSearchResults([]);
    setSearchQuery('');
  };
  if (isElectron() && token.accessToken) {
    // Still fetching flags → show loader
    if (isLoading) return <RootLoader />;

    // Only reset wizard state if this is truly a fresh install, not just an empty library
    const hasEverCompletedSetup = localStorage.getItem('hasCompletedSetup');
    const shouldResetWizard =
      wizardDismissed &&
      !hasScannedTracks &&
      !isLoading &&
      !hasEverCompletedSetup;

    if (shouldResetWizard) {
      console.log('Fresh install detected - resetting wizard state');
      localStorage.removeItem('wizardDismissed');
      localStorage.removeItem('hasScannedTracks');
      localStorage.removeItem('musicSource');
      dispatch(cancelWizardDismissed());
      // Don't redirect immediately, let the next render cycle handle it
      return <RootLoader />;
    }

    // Haven’t completed onboarding → shove them to /setup
    // Simplify gating to rely primarily on explicit completion flag;
    // avoid bouncing users back to the wizard while scans/imports run in background.
    const needsSetup = !hasEverCompletedSetup && !wizardDismissed;

    if (needsSetup && location.pathname !== '/setup') {
      return <Redirect to="/setup" />;
    }
  }

  const isSetup = location.pathname === '/setup';

  return (
    <PostHogProvider
      apiKey={config.POSTHOG_API_KEY}
      options={{
        api_host: config.POSTHOG_HOST,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-mask]',
        },
      }}
    >
      <Profiler id="App" onRender={onRenderCallback}>
        <GlobalAlertProvider>
          <ScrollProvider>
            <AlertOverride />
            <AppReleaseModals showChangelog={!isSetup} />
            <DriveStateSubscriber />
            <RootLoader />
            <ToastManager />
            <KartraUpgrade />
            <ImportProgressConnector />
            <VdjCacheIndexBootstrap />
            <MembershipClaimsBootstrap />
            <BreakpointContextProvider>
              <ImportProgressProvider>
                <LibraryStatsProvider>
                  {token.accessToken ? (
                    <>
                      {!isSetup && <ClientSupportBanner />}
                      {!isSetup && !showLogin && (
                        <Header
                          searchQuery={searchQuery}
                          setSearchQuery={setSearchQuery}
                        />
                      )}
                      <IonSplitPane
                        when={isSetup ? false : 'xl'}
                        contentId="main"
                        className={isSetup ? 'no-offset' : ''}
                      >
                        {!isSetup && <Menu />}
                        {searchQuery.trim().length > 0 && (
                          <SearchResults
                            crateData={crateSearchResults}
                            songData={songSearchResults}
                            handleItemClicked={handleItemClick}
                            searchLoading={searchLoading}
                            handleClose={handleSearchClose}
                          />
                        )}
                        {showSettings && <Preferences />}
                        <AgentChatSessionProvider>
                          {privateRoutes(token, history, dispatch)}
                        </AgentChatSessionProvider>
                      </IonSplitPane>
                      {!isSetup && <Footer />}
                      <StickyPlayer />
                      {showLogin && (
                        <LoginOverlay setShowLogin={setShowLogin} />
                      )}
                    </>
                  ) : (
                    <>{publicRoutes()}</>
                  )}
                </LibraryStatsProvider>
              </ImportProgressProvider>
            </BreakpointContextProvider>
          </ScrollProvider>
        </GlobalAlertProvider>
      </Profiler>
    </PostHogProvider>
  );
};
export default App;
